import { toast } from "react-toastify"
import dayjs from "dayjs"

import { adminApi } from "store/api/adminApi"
import { alertsApi } from "store/api/alertsApi"
import { authApi } from "store/api/authApi"
import { clientApi } from "store/api/clientApi"
import { contractorsApi } from "store/api/contractorsApi"
import { dashboardApi, useLazyGetThemeQuery } from "store/api/dashboardApi"
import { devicesApi } from "store/api/devicesApi"
import { dictionaryApi } from "store/api/dictionaryApi"
import { enterpriseApi } from "store/api/enterpriseApi"
import { monitoringApi } from "store/api/monitoringApi"
import { notificationApi } from "store/api/notificationApi"
import { ordersApi } from "store/api/ordersApi"
import { performanceTestApi } from "store/api/performanceTestApi"
import { profileApi, useLazyGetUserProfileQuery } from "store/api/profileApi"
import { vendorApi } from "store/api/vendorApi"
import { warehouseManagerApi } from "store/api/warehouseManagerApi"
import { warehousesApi } from "store/api/warehousesApi"
import { widgetsApi } from "store/api/widgetsApi"
import { useAppDispatch } from "store/hooks"
import { setUserActiveTheme, setUserLogo } from "store/tempDataSlise"
import { setUserAuthorized, setUserProfile, setUserTemperatureScale } from "store/userSlise"
import { ContractorThemeKey } from "constants/ContractorThemes"
import { StorageKeys } from "types/enums/StorageKeys"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { UserRole } from "utils/checkRoleType"
import getErrorMessage from "utils/getErrorMessage"
import { getStorageItem, removeStorageItem, setStorageItem } from "utils/localStorage"

const tokenScopedApis = [
  adminApi,
  alertsApi,
  authApi,
  clientApi,
  contractorsApi,
  dashboardApi,
  devicesApi,
  dictionaryApi,
  enterpriseApi,
  monitoringApi,
  notificationApi,
  ordersApi,
  performanceTestApi,
  profileApi,
  vendorApi,
  warehouseManagerApi,
  warehousesApi,
  widgetsApi,
]

export const useLogin = () => {
  const [triggerGetProfile] = useLazyGetUserProfileQuery()
  const [triggerGetTheme] = useLazyGetThemeQuery()
  const dispatch = useAppDispatch()

  const handleThemeChange = (themeKey?: ContractorThemeKey, logo?: string | null) => {
    dispatch(setUserActiveTheme(themeKey || "default"))
    dispatch(setUserLogo(logo || undefined))
  }

  const getProfile = async () => {
    try {
      const res = await triggerGetProfile()
      if (!res.data?.data) {
        return
      }

      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        const lastSync = dayjs()
        const data = { ...res?.data?.data, lastSync }
        const userTemperatureScale = getStorageItem(StorageKeys.UserTemperatureScale)
        const temperatureScale = data?.contractor?.country?.id === 1 ? "fahrenheit" : "celsius"
        setStorageItem(StorageKeys.UserProfile, data)

        if (!userTemperatureScale) {
          setStorageItem(StorageKeys.UserTemperatureScale, temperatureScale)
        }
        dispatch(setUserProfile(data))
        dispatch(setUserAuthorized(true))
        dispatch(setUserTemperatureScale(userTemperatureScale || temperatureScale))

        const profile = res.data?.data
        const contractor = profile?.contractor
        const shouldLoadTheme =
          contractor?.subscription_plan === SubscriptionPlan.E_PLUS || profile?.type === UserRole.EnterpriseManager

        if (shouldLoadTheme) {
          const themeResult = await triggerGetTheme()
          if (themeResult.data?.theme) {
            try {
              const theme = JSON.parse(themeResult.data.theme)
              handleThemeChange(theme.themeKey, themeResult.data.logo)
            } catch {
              handleThemeChange()
            }
          } else if (themeResult.data?.logo) {
            handleThemeChange(undefined, themeResult.data.logo)
          } else {
            handleThemeChange()
          }
        } else {
          handleThemeChange()
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const login = async ({ accessToken, masterToken }: { accessToken?: string | null; masterToken?: string | null }) => {
    if (masterToken) {
      setStorageItem(StorageKeys.EnterpriseMasterAccountToken, masterToken)
    } else {
      removeStorageItem(StorageKeys.EnterpriseMasterAccountToken)
    }

    if (accessToken) {
      setStorageItem(StorageKeys.Token, accessToken)
      tokenScopedApis.forEach(api => dispatch(api.util.resetApiState()))
      await getProfile()
    }
  }

  const getCurrentAuthToken = () => getStorageItem(StorageKeys.Token)

  const getEnterpriseMasterAccountToken = () => getStorageItem(StorageKeys.EnterpriseMasterAccountToken)

  return { login, getCurrentAuthToken, getEnterpriseMasterAccountToken }
}
